/**
 * 지도 색 구간 — **분위 구간이되 경계를 반드시 인쇄한다.**
 *
 * 왜 선형 램프가 아닌가. 실측 가동률이 0.98~1.00 에 몰려 있어 선형 램프로 칠하면
 * 전국이 한 색이 된다 (§8.6.3 과 같은 문제의 지도 버전). 커버리지는 0.13~1.00 로
 * 잘 퍼지지만 사용자가 지표를 바꿀 수 있으므로 규칙이 지표에 따라 달라지면 안 된다.
 *
 * 왜 분위 구간이 정직한가. 분위는 "면적 대비 값"을 왜곡하지만, **경계값을 범례에
 * 인쇄하면** 읽는 사람이 왜곡을 되짚을 수 있다. 경계를 숨긴 분위 지도가 거짓말이다.
 * 그래서 이 모듈은 경계를 **반환값의 일부**로 강제한다 — 색만 돌려주는 API 를 두지 않는다.
 */

/** 단일 색상 순차 램프 — 명도만 단조 증가. 무지개·다색 금지 (§8.6.2) */
export const SEQ_BLUE = ['#dbe9f8', '#a9cbee', '#6ea7dd', '#3a7fc4', '#0b4f8f'] as const

export interface Bins {
  /** 구간 경계 k-1 개 (k = 색 수). 오름차순. */
  edges: number[]
  /** 각 구간의 색 */
  colors: readonly string[]
  /** 각 구간에 들어간 개수 — 분위가 실제로 균등했는지 보여준다 */
  counts: number[]
  /** 값이 null 인 대상 수. 색을 주지 않고 해칭으로 표시한다 */
  nMissing: number
  /** 값 → 구간 인덱스 */
  indexOf: (v: number | null | undefined) => number | null
}

/**
 * 분위 구간을 만든다. 값이 한 점에 뭉쳐 있으면 (경계가 겹치면) 구간을 **줄인다** —
 * 같은 경계 두 개를 서로 다른 색으로 칠하면 없는 차이를 만든 것이다.
 */
export function quantileBins(
  values: (number | null | undefined)[],
  colors: readonly string[] = SEQ_BLUE,
): Bins {
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v))
  const nMissing = values.length - finite.length
  if (finite.length === 0) {
    return {
      edges: [],
      colors: colors.slice(0, 1),
      counts: [0],
      nMissing,
      indexOf: () => null,
    }
  }

  const sorted = [...finite].sort((a, b) => a - b)
  const raw: number[] = []
  for (let i = 1; i < colors.length; i += 1) {
    const at = Math.floor((sorted.length * i) / colors.length)
    raw.push(sorted[Math.min(at, sorted.length - 1)] as number)
  }
  // 중복 경계 제거 — 값이 뭉쳐 있으면 색 수가 줄어드는 것이 맞다
  const edges = [...new Set(raw)].filter((e) => e > (sorted[0] as number))
  const used = colors.slice(0, edges.length + 1)

  const indexOf = (v: number | null | undefined): number | null => {
    if (v == null || !Number.isFinite(v)) return null
    let i = 0
    while (i < edges.length && v >= (edges[i] as number)) i += 1
    return i
  }

  const counts = new Array<number>(used.length).fill(0)
  for (const v of finite) {
    const i = indexOf(v)
    if (i != null) counts[i] = (counts[i] ?? 0) + 1
  }

  return { edges, colors: used, counts, nMissing, indexOf }
}

/** `#rrggbb` → deck.gl 의 `[r,g,b]`. canvas 는 CSS 색 문자열을 못 받는다. */
export function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
